import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LeagueRegistrationSliderProps {
  isOpen: boolean;
  onClose: () => void;
}

const LeagueRegistrationSlider: React.FC<LeagueRegistrationSliderProps> = ({ 
  isOpen, 
  onClose
}) => {
  // Klaviyo List ID hardcoded or from environment variable
  const klaviyoListId = process.env.REACT_APP_KLAVIYO_LIST_ID ;
  const [mounted, setMounted] = useState(false);
  const [isCommissioner, setIsCommissioner] = useState<boolean | null>(null);
  const [isSignedUp, setIsSignedUp] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  // Handle mounting animation
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else {
      const timer = setTimeout(() => {
        setMounted(false);
        setIsCommissioner(null); // Reset state when closing
        setIsSignedUp(false);
        setEmail('');
        setMessage('');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);
  
    try {
      const trimmedEmail = email.toLowerCase().trim();
      
      // This is the correct path when deployed to Netlify
      const response = await fetch('/.netlify/functions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: trimmedEmail,
          listId: klaviyoListId
        })
      });
      
      // Handle the response
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `Subscription failed with status: ${response.status}`);
      }
      
      setMessage('Successfully subscribed!');
      setEmail('');
      setIsSignedUp(true);
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      setMessage(`Error: ${error.message || 'Failed to subscribe. Please try again.'}`);
      setIsSignedUp(false);
    } finally {
      setIsLoading(false);
    }
  };

  // For development/testing without backend
  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);
  
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }
      
      setMessage('Successfully subscribed!');
      setEmail('');
      setIsSignedUp(true);
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      setMessage(`Error: ${error.message || 'Failed to subscribe. Please try again.'}`);
      setIsSignedUp(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommissionerClick = () => {
    onClose(); // Close the slider
    navigate('/signup'); // Navigate to sign-up page
  };

  if (!mounted) return null;

  const renderNewsletterForm = () => (
    <>
      <div className="drawerHeading">
        Sign up for the Fantasy Loser newsletter and receive draft order ideas, punishment content, and updates on new features.
      </div>
      <form onSubmit={process.env.NODE_ENV === 'development' ? handleDemoSubmit : handleSubmit}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="example: HingleMcCringleberry@pennstate.edu"
          className="fldStyle"
          required
        />
        <button 
          type="submit" 
          disabled={isLoading}
          className="btnStyle"
        >
          {isLoading ? 'Submitting...' : 'Submit'}
        </button>

        {message && (
          <p className={`text-sm ${
            message.startsWith('Error') ? 'text-red-500' : 'text-[#18f7b1]'
          }`}>
            {message}
          </p>
        )}
      </form>
    </>
  );

  const renderCommissionerChoice = () => (
    <div className="min-h-full py-16 px-4 md:px-16 flex flex-col justify-center">
      <div className="max-w-xl mx-auto w-full text-left">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 mb-6 sm:mb-8">
          <h1 className="font-antonio text-[#18f7b1] text-4xl sm:text-5xl md:text-[60px] font-bold leading-tight sm:leading-[80px]">
            IT'S GOOD!
          </h1>
          <img 
            src={require('../../assets/giphy-1 1.png')} 
            alt="Fantasy Loser It's Good" 
            className="mt-4 sm:mt-0 w-[200px] h-[160px] sm:w-[264px] sm:h-[210px]"
          />
        </div>
        <h2 className="font-antonio text-white text-2xl sm:text-3xl md:text-[48px] mb-4 leading-tight sm:leading-[48px]">
          Thanks for joining the Fantasy Loser community—big things are coming.
        </h2>
        <h2 className="font-antonio text-white text-2xl sm:text-3xl md:text-[48px] mb-4 leading-tight sm:leading-[48px]">
          Want early access to help shape the future of Fantasy Loser?
        </h2>
        
        <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 mt-8 sm:mt-12">
          <button
            onClick={handleCommissionerClick}
            className="border-2 border-[#18f7b1] text-white px-6 py-3 sm:px-8 sm:py-4 
                     rounded-[10px] hover:bg-transparent hover:text-[#18f7b1] transition-colors duration-300
                     font-antonio text-lg sm:text-xl"
          >
            Waitlist for early access
          </button>
          
          <button
            onClick={onClose}
            className="border-2 border-white text-white px-6 py-3 sm:px-8 sm:py-4 
                     rounded-[10px] hover:bg-white hover:text-black transition-colors duration-300
                     font-antonio text-lg sm:text-xl"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (!isSignedUp) {
      return renderNewsletterForm();
    }
    
    if (isCommissioner === null) {
      return renderCommissionerChoice();
    }
    
    return null; // Remove typeform rendering since we're navigating instead
  };

  return (
    <div className="popupWrap">
      <div className="drawerWrap">
        <button
          onClick={onClose}
          className="closePopup"
          aria-label="Close registration panel"
        >
          <X size={24} />
        </button>

        {/* Content */}
        <div className="drawerCont">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default LeagueRegistrationSlider;